import { code } from "@spscommerce/utils";

export const SpsSelectExamples = {
    basic: {
        label: "Basic",
        description: "info about basic selects",
        examples: {
            simple: {
                description: "Simple value options",
                template: code`
                    <sps-select id="basic" [options]="colors" [(ngModel)]="selectedColor"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colors = ["red", "blue", "green"];
                    }
                `
            },
            placeholder: {
                description: "Custom placeholder text",
                template: code`
                    <sps-select id="placeholder" [options]="colors" placeholder="Choose a color…"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colors = ["red", "blue", "green"];
                    }
                `
            },
            notClearable: {
                description: "Not clearable",
                template: code`
                    <sps-select [options]="colors"
                      notClearable
                      [(ngModel)]="selectedColorNotClearable"
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colors = ["red", "blue", "green"];
                        selectedColorNotClearable = "red";
                    }
                `
            },
            zeroState: {
                description: "Zero state",
                template: code`
                    <sps-select [options]="[]" zeroState="We're sorry, there are no options to choose from at this time, because reasons."></sps-select>
                `
            },
            withLabel: {
                description: "Usage with @spscommerce/ds-ng-forms",
                template: code`
                    <form [formGroup]="form">
                        <sps-label for="color">Color</sps-label>
                        <sps-select formControlName="color" [options]="colors"></sps-select>
                    </form>
                `,
                class: code`
                    class DemoComponent {
                        form: FormGroup;
                        colors = ["red", "blue", "green"];
                        constructor(fb: FormBuilder) {
                            this.form = fb.group({
                                color: [null, Validators.required]
                            });
                            this.form.get("color").markAsDirty();
                        }
                    }
                `
            },
            disabled: {
                description: "Disabled",
                template: code`
                    <sps-select [options]="colors" disabled [(ngModel)]="selectedColorDisabled"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colors = ["red", "blue", "green"];
                        selectedColorDisabled = "red";
                    }
                `
            }
        }
    },
    objectOpts: {
        label: "Object Options",
        description: "info about object options",
        examples: {
            basic: {
                description: "Basic use of object options",
                template: code`
                    <sps-select [options]="sizes" textKey="size" [(ngModel)]="selectedSize"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        sizes = [
                            { size: "S", price: "10.99" },
                            { size: "M", price: "12.99" },
                            { size: "L", price: "14.99" }
                        ];
                    }
                `
            },
            captions: {
                description: "Captions",
                template: code`
                    <sps-select [options]="lorem"
                      textKey="title"
                      captionKey="description"
                      [(ngModel)]="selectedLorem"
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        lorem = [
                            {
                                title: "Lorem",
                                description: "Ut enim ad minim veniam"
                            },
                            {
                                title: "Ipsum",
                                description: "Excepteur sint occaecat cupidatat non proident"
                            }
                        ];
                        selectedLorem;
                    }
                `
            }
        }
    },
    searchable: {
        label: "Search/Filter",
        description: "info about search/filter",
        examples: {
            simple: {
                description: "Simple search function",
                template: code`
                    <sps-select id="search-function" [options]="colorSearch"
                      [(ngModel)]="selectedSearchedColor"
                      zeroState="While I'm sure that's a lovely color, it's not an option we support."
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colorSearch(search: string) {
                            return [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                            .filter(s => (new RegExp(search, "i")).test(s));
                        };
                    }
                `
            },
            promise: {
                description: "Search function returning a Promise",
                template: code`
                    <sps-select [options]="colorSearchPromise"
                      [(ngModel)]="selectedPromisedColor">
                    </sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colorSearchPromise(search: string) {
                            return new Promise((resolve, reject) => {
                                const result = [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                                    .filter(s => (new RegExp(search, "i")).test(s));
                                resolve(result);
                            });
                        }
                    }
                `
            },
            observable: {
                description: "Search function returning an Observable",
                template: code`
                    <sps-select [options]="colorSearchObservable"
                      [(ngModel)]="selectedObservedColor">
                    </sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colorSearchObservable(search: string) {
                            const result = [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                                .filter(s => (new RegExp(search, "i")).test(s));
                            return of(result); // of is rxjs oberservableOf
                        }
                    }
                `
            },
            debounce: {
                description: "Search with Debounce",
                template: code`
                    <sps-select id="debounce-select" [options]="colorSearch"
                      [(ngModel)]="selectedSearchedColor"
                      zeroState="While I'm sure that's a lovely color, it's not an option we support."
                      searchDebounce="2000"
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colorSearch(search: string) {
                            return [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                            .filter(s => (new RegExp(search, "i")).test(s));
                        }
                    }
                `
            }
        }
    },
    specialAction: {
        label: "Simple Action",
        description: "info about simple action selects",
        examples: {
            basic: {
                description: "Basic",
                template: code`
                    <sps-select id="special-action" [options]="colors"
                        [action]="newColor"
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        colors = ["red", "blue", "green"];
                        @SpsAction({
                            icon: "plus-sign",
                            label: "Create a New Color"
                        })
                        newColor() {
                            console.log("newColor() called!\\n");
                        };
                    }
                `
            },
            link: {
                description: "Link",
                template: code`
                    <sps-select [options]="colorSearch" [action]="linkAction"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        linkAction: SpsActionDescriptor = {
                            icon: "plus-sign",
                            label: "Create a New Color",
                            isLink: true,
                            href: "https://google.com"
                        };
                        colorSearch(search: string) {
                            return [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                            .filter(s => (new RegExp(search, "i")).test(s));
                        };
                    }
                `
            },
            linkFunction: {
                description: "Function that returns a link",
                template: code`
                    <sps-select [options]="colorSearch" [action]="newColorLink"></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        @SpsAction({
                            icon: "plus-sign",
                            label: "Create a New Color",
                            isLink: true
                        })
                        newColorLink() {
                            return \`https://www.google.com/search?q=0\`;
                        }
                        colorSearch(search: string) {
                            return [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                            .filter(s => (new RegExp(search, "i")).test(s));
                        };
                    }
                `
            },
            dynamicLabel: {
                description: "Dynamic label",
                template: code`
                    <sps-select #dynamicLabelSelect
                      [options]="colorSearch"
                      [action]="createFromSearch"
                    ></sps-select>
                `,
                class: code`
                    class DemoComponent {
                        constructor() {}
                        @ViewChild("dynamicLabelSelect", { static: false })
                        dynamicLabelSelect: SpsSelectComponent<string>;

                        @SpsAction({
                            icon: "plus-sign",
                            label(cpt: SelectDemoComponent) {
                                return cpt.dynamicLabelSelect && cpt.dynamicLabelSelect.search
                                    ? \`Create "\${cpt.dynamicLabelSelect.search}"\`
                                    : "";
                            }
                        })
                        createFromSearch() {
                        };
                        colorSearch(search: string) {
                            return [ "red", "orange", "yellow", "green", "blue", "indigo", "violet" ]
                            .filter(s => (new RegExp(search, "i")).test(s));
                        };
                    }
                `
            }
        }
    }
};
